import { defineComponent as _defineComponent } from 'vue'
import Loader from '@/components/common/Loader.vue';
import FogoNightlyBanner from '@/components/common/FogoNightlyBanner.vue';
import { computed, nextTick, ref, watch, onMounted } from 'vue';
import Toast from './components/common/Toast.vue';
import { useSettingsStore } from '@/pinia/settingsStore';
import { useAlertStore } from './pinia/alertStore';
import { useNetworkStore } from './pinia/networkStore';
import { FLUXB_MINT, SOL_MINT } from './shared/tokenIdentity';
import store, { ensureHeavyStoreModules } from './store';
import { isSolana, getAvailableNetworks } from './shared/appEnvironment';
import router from './router';
import { ensureGlobalStyles } from './shared/globalStyles';
import { shouldStartDeferredRuntimeForRoute } from './shared/homeRuntimePolicy';

declare global {
	interface Window {
		showShortcutHelp?: () => void;
		closeShortcutHelp?: () => void;
		changeChartInterval?: (interval: string) => void;
		isMobileDevice?: boolean;
		handleNetworkParameter?: () => void;
	}
}


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const Navbar = () => import(/* webpackChunkName: "shell-navbar" */ '@/components/Navbar.vue');
const AddressExplorer = () =>
	import(
		/* webpackChunkName: "shell-address-explorer" */ '@/components/common/AddressExplorer.vue'
	);
const KeyboardShortcuts = () =>
	import(
		/* webpackChunkName: "shell-keyboard-shortcuts" */ '@/components/common/KeyboardShortcuts.vue'
	);
const ShortcutHelpModal = () =>
	import(/* webpackChunkName: "shell-shortcut-help" */ '@/components/common/ShortcutHelpModal.vue');
const NetworkSwitchModal = () =>
	import(
		/* webpackChunkName: "shell-network-switch" */ '@/components/common/NetworkSwitchModal.vue'
	);
const SignatureStatusModal = () =>
	import(
		/* webpackChunkName: "shell-signature-status" */ './components/transaction/SignatureStatusModal.vue'
	);
const TransactionsHistory = () =>
	import(
		/* webpackChunkName: "shell-transactions-history" */ './components/transaction/TransactionsHistory.vue'
	);

// refs
const shortcutHelpModalRef = ref(null);
const networkSwitchModalRef = ref(null);
const navigationReady = ref(false);
const deferredShellReady = ref(false);
const initialRouteNeedsRuntime = shouldStartDeferredRuntimeForRoute({
	path: window.location.pathname,
	hasNetworkParameter: new URLSearchParams(window.location.search).has('network'),
});
const routeContentReady = ref(!initialRouteNeedsRuntime);

// methods
function showShortcutHelp(): void {
	if (shortcutHelpModalRef.value) {
		shortcutHelpModalRef.value.show();
	}
}

function closeShortcutHelp(): void {
	if (shortcutHelpModalRef.value) {
		shortcutHelpModalRef.value.close();

		// Dispatch an event to notify that the modal was closed
		document.dispatchEvent(new CustomEvent('shortcut-help-closed'));
	}
}

// Function to change chart interval
function changeChartInterval(interval: string): void {
	// Find all chart components on the page
	const chartComponents = document.querySelectorAll('.chart-container');

	// Dispatch a custom event if chart components exist
	if (chartComponents.length > 0) {
		const event = new CustomEvent('change-chart-interval', { detail: { interval } });
		document.dispatchEvent(event);
	}
}

// Add type declaration for window object
async function getCacheStore() {
	const { useCacheStore } = await import('@/pinia/cacheStore');
	return useCacheStore();
}

let deferredRuntimePromise: Promise<void> | null = null;

function ensureDeferredRuntime(): Promise<void> {
	if (deferredRuntimePromise) return deferredRuntimePromise;

	deferredRuntimePromise = initializeHeavyRuntime()
		.then(async () => {
			deferredShellReady.value = true;
			await nextTick();
			handleNetworkParameter();
		})
		.catch((error) => {
			deferredRuntimePromise = null;
			console.error('Failed to initialize deferred application runtime', error);
			throw error;
		});

	return deferredRuntimePromise;
}

async function prepareRouteRuntime(path: string, hasNetworkParameter = false): Promise<void> {
	if (
		deferredShellReady.value ||
		!shouldStartDeferredRuntimeForRoute({ path, hasNetworkParameter })
	) {
		routeContentReady.value = true;
		return;
	}

	// Route components such as TokenShow depend on the deferred Solana/cache runtime
	// during their own mount. Keep them out of the tree until that runtime is ready;
	// the landing page still renders immediately and keeps its performance win.
	routeContentReady.value = false;
	try {
		await ensureDeferredRuntime();
	} finally {
		routeContentReady.value = true;
	}
}

async function activateDeferredTransactions(): Promise<void> {
	await ensureDeferredRuntime();
	await nextTick();

	if (window.innerWidth >= 640) return;
	const mobileToggle = document.querySelector<HTMLButtonElement>(
		'button[aria-label="Toggle transactions history"]'
	);
	mobileToggle?.click();
}

// Global keyboard handler for all hotkeys
async function handleGlobalKeydown(event: KeyboardEvent): Promise<void> {
	// Get settings
	const settings = settingsStore.settings;

	// Check if keyboard shortcuts are enabled
	if (!settings.keyboardShortcuts || !settings.keyboardShortcuts.enabled) {
		return;
	}

	// Check if the user is focused on a keyboard shortcut input field
	// These are the inputs where users configure their shortcut keys in settings
	const target = event.target as HTMLElement;
	const isShortcutInput =
		target &&
		target.tagName === 'INPUT' &&
		target.classList.contains('input') &&
		target.closest('tr') &&
		(target.closest('tr').querySelector('td:first-child')?.textContent?.includes('Shortcut') ||
			target
				.closest('tr')
				.querySelector('td:first-child')
				?.textContent?.includes('Open token search') ||
			target.closest('tr').querySelector('td:first-child')?.textContent?.includes('Sell token') ||
			target.closest('tr').querySelector('td:first-child')?.textContent?.includes('Buy token') ||
			target
				.closest('tr')
				.querySelector('td:first-child')
				?.textContent?.includes('Cycle interval') ||
			target.closest('tr').querySelector('td:first-child')?.textContent?.includes('Open help'));

	// If user is focused on a shortcut input field, don't trigger shortcuts
	if (isShortcutInput) {
		return;
	}

	// Get the search shortcut key
	const searchKey = settings.keyboardShortcuts.shortcuts?.search?.key || '=';

	// If the key matches the search shortcut
	if (event.key === searchKey) {
		// Check if the search shortcut is specifically enabled
		const searchShortcut = settings.keyboardShortcuts.shortcuts?.search;
		if (!searchShortcut || !searchShortcut.enabled) {
			// Search shortcut is disabled individually, don't process it
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		void ensureDeferredRuntime();
		const cacheStore = await getCacheStore();
		const isHelpModalOpen = document.querySelector('.shortcut-help-modal') !== null;

		// If help modal is open, close it first
		if (isHelpModalOpen) {
			if (window.closeShortcutHelp) {
				window.closeShortcutHelp();
			}

			// Wait a tiny bit before opening search modal
			setTimeout(() => {
				cacheStore.setListState(true);
			}, 10);
		} else {
			// Toggle the token list directly
			cacheStore.setListState(!cacheStore.listState);
		}

		return;
	}

	// Get the configured help shortcut key
	const helpKey = settings.keyboardShortcuts.shortcuts?.help?.key || '/';

	// Check if the pressed key matches the configured help shortcut key
	if (event.key === helpKey) {
		// Only handle if help shortcut is enabled
		if (settings.keyboardShortcuts.shortcuts?.help?.enabled) {
			event.preventDefault();
			event.stopPropagation();

			await ensureDeferredRuntime();
			await nextTick();
			const cacheStore = await getCacheStore();
			const isHelpModalOpen = document.querySelector('.shortcut-help-modal') !== null;
			const isSearchModalOpen =
				document.querySelector('.token-select-list') !== null || cacheStore.listState;

			// If search modal is open, close it first
			if (isSearchModalOpen) {
				cacheStore.setListState(false);

				// Wait a tiny bit before opening help modal
				setTimeout(() => {
					if (window.showShortcutHelp) {
						window.showShortcutHelp();
					}
				}, 10);
			} else if (isHelpModalOpen) {
				// If help modal is already open, close it
				if (window.closeShortcutHelp) {
					window.closeShortcutHelp();
				}
			} else {
				// If no modal is open, open help modal
				if (window.showShortcutHelp) {
					window.showShortcutHelp();
				}
			}

			return;
		}
	}
}

// expose to window for global access
if (typeof window !== 'undefined') {
	window.showShortcutHelp = showShortcutHelp;
	window.closeShortcutHelp = closeShortcutHelp;
	window.changeChartInterval = changeChartInterval;

	// Add a global flag to check if device is mobile
	window.isMobileDevice =
		/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
		window.innerWidth <= 768;

	// Add resize event listener to update mobile status
	window.addEventListener('resize', () => {
		window.isMobileDevice =
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
			window.innerWidth <= 768;
	});

	// Add global keyboard handler with highest priority
	document.addEventListener('keydown', handleGlobalKeydown, true);
}

// computed
const isBot = computed<(path: string) => boolean>(() => (path: string) => {
	return path.indexOf('/bot') > -1;
});

// Banner state management
const bannerDismissed = ref(false);

const shouldShowFogoBanner = computed(() => {
	if (!networkStore.isFogo) return false;
	if (bannerDismissed.value) return false;
	const dismissed = localStorage.getItem('fogo-nightly-banner-dismissed');
	return dismissed !== 'true';
});

// Handle banner dismiss
function handleBannerDismiss() {
	bannerDismissed.value = true;
}

// data
const settingsStore = useSettingsStore();
const alertStore = useAlertStore();
const networkStore = useNetworkStore();
const legacyCacheLoading = computed<boolean>(() => Boolean(store.state.cache?.loading));

let heavyRuntimePromise: Promise<void> | null = null;

function initializeHeavyRuntime(): Promise<void> {
	if (heavyRuntimePromise) return heavyRuntimePromise;

	heavyRuntimePromise = (async () => {
		await ensureHeavyStoreModules();

		const [{ useCacheStore }, { useSolanaStore }, { useWalletStore }, { useSwapStore }] =
			await Promise.all([
				import('@/pinia/cacheStore'),
				import('@/pinia/solanaStore'),
				import('@/pinia/walletStore'),
				import('@/pinia/swapStore'),
			]);

		const cacheStore = useCacheStore();
		const solanaStore = useSolanaStore();
		const walletStore = useWalletStore();
		const swapStore = useSwapStore();

		store.commit(
			'solana/set_endpoint',
			store.state.settings.customRpcUrl || store.state.rpc.rpcUrl
		);
		store.dispatch('solana/fetchClusterFees');
		cacheStore.fetchNameMap();

		solanaStore.init(settingsStore.settings.customRpcUrl || store.state.rpc.rpcUrl);
		solanaStore.fetchClusterFees();
		cacheStore.fetchMintPriceUSD(SOL_MINT);

		if (isSolana()) {
			cacheStore.fetchMintPriceUSD(FLUXB_MINT);
		}

		watch(
			() => walletStore.isConnected,
			() => {
				swapStore.getTransactionsHistory(walletStore.address?.toString());
			}
		);
	})();

	return heavyRuntimePromise;
}

// Keyboard shortcuts are now disabled by default
// Settings will be loaded from localStorage if available

// Network parameter handling
async function handleNetworkParameterAsync(): Promise<void> {
	const urlParams = new URLSearchParams(window.location.search);
	const networkParam = urlParams.get('network');

	if (!networkParam) return;

	// Check if prompt parameter is set to true
	const promptParam = urlParams.get('prompt');
	const shouldPrompt = promptParam === 'true';

	// Normalize the network parameter (case-insensitive)
	const normalizedParam = networkParam.toUpperCase();

	// Map common network names to network IDs
	const networkMap: Record<string, string> = {
		FOGO: 'FOGO_MAINNET',
		SOLANA: 'SOL_MAINNET',
		SOL: 'SOL_MAINNET',
		FOGO_MAINNET: 'FOGO_MAINNET',
		SOL_MAINNET: 'SOL_MAINNET',
		FOGO_TESTNET: 'FOGO_TESTNET',
	};

	const targetNetworkId = networkMap[normalizedParam];

	// Check if the network exists and is available
	if (!targetNetworkId) {
		console.warn(`Unknown network parameter: ${networkParam}`);
		return;
	}

	const availableNetworks = getAvailableNetworks();
	const targetNetwork = availableNetworks.find((n) => n.id === targetNetworkId);

	if (!targetNetwork) {
		console.warn(`Network ${networkParam} is not available in this environment`);
		return;
	}

	// If the target network is different from current
	if (targetNetworkId !== networkStore.currentNetworkId) {
		const { useWalletStore } = await import('@/pinia/walletStore');
		const walletStore = useWalletStore();

		// Guard clause: Check if user is logged in with FluxBot
		if (walletStore.isBotWallet) {
			// Don't auto-switch for FluxBot users - they must change network in Telegram
			console.warn('FluxBot users cannot change network via URL parameter');
			return;
		}

		if (shouldPrompt) {
			// Show modal for user confirmation
			if (networkSwitchModalRef.value) {
				networkSwitchModalRef.value.show(targetNetworkId);
			}
		} else {
			// Auto-switch without confirmation
			await networkStore.changeNetwork(targetNetworkId);
		}
	}
}

function handleNetworkParameter(): void {
	void handleNetworkParameterAsync();
}

// Expose to window for global access
if (typeof window !== 'undefined') {
	window.handleNetworkParameter = handleNetworkParameter;
}

router.beforeEach(async (to, _from, next) => {
	try {
		await prepareRouteRuntime(to.path);
		next();
	} catch (error) {
		next(error);
	}
});

if (initialRouteNeedsRuntime) {
	void prepareRouteRuntime(
		window.location.pathname,
		new URLSearchParams(window.location.search).has('network')
	);
}

// Setup network parameter detection on mount and route changes
onMounted(() => {
	// Check banner dismissed state from localStorage
	const dismissed = localStorage.getItem('fogo-nightly-banner-dismissed');
	bannerDismissed.value = dismissed === 'true';

	const startDeferredRuntime = () => {
		void ensureDeferredRuntime();
	};

	// Routes that need the application runtime should not wait for the home-page load boundary.
	router.afterEach((to) => {
		if (shouldStartDeferredRuntimeForRoute({ path: to.path })) startDeferredRuntime();
		if (deferredShellReady.value) handleNetworkParameter();
	});

	const revealNavigation = async () => {
		await ensureGlobalStyles();
		navigationReady.value = true;
		const hasNetworkParameter = new URLSearchParams(window.location.search).has('network');
		const currentPath = router.currentRoute?.path || window.location.pathname;

		if (!shouldStartDeferredRuntimeForRoute({ path: currentPath, hasNetworkParameter })) {
			window.addEventListener('pointerdown', startDeferredRuntime, { once: true, passive: true });
			return;
		}

		startDeferredRuntime();
	};

	if (typeof window.requestAnimationFrame === 'function') {
		window.requestAnimationFrame(() => {
			void revealNavigation();
		});
	} else {
		void revealNavigation();
	}
});

return { __sfc: true,Navbar, AddressExplorer, KeyboardShortcuts, ShortcutHelpModal, NetworkSwitchModal, SignatureStatusModal, TransactionsHistory, shortcutHelpModalRef, networkSwitchModalRef, navigationReady, deferredShellReady, initialRouteNeedsRuntime, routeContentReady, showShortcutHelp, closeShortcutHelp, changeChartInterval, getCacheStore, deferredRuntimePromise, ensureDeferredRuntime, prepareRouteRuntime, activateDeferredTransactions, handleGlobalKeydown, isBot, bannerDismissed, shouldShowFogoBanner, handleBannerDismiss, settingsStore, alertStore, networkStore, legacyCacheLoading, heavyRuntimePromise, initializeHeavyRuntime, handleNetworkParameterAsync, handleNetworkParameter, Loader, FogoNightlyBanner, Toast, store }
}

})